import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { adminList, adminUpsert, adminDelete, activateRuleset } from "@/lib/admin-registry.functions";

export const Route = createFileRoute("/_authenticated/app/admin")({
  ssr: false,
  component: AdminCenter,
});

type Section = {
  key: string;
  label: string;
  table:
    | "manufacturers" | "product_groups" | "products" | "product_prices"
    | "product_warranties" | "product_compatibility" | "ekd_products"
    | "feature_flags" | "system_config" | "subsidy_programs" | "subsidy_rules"
    | "subsidy_sources" | "tax_rules" | "energy_price_scenarios"
    | "calc_ruleset_versions" | "calc_formulas" | "prompts";
  template?: Record<string, unknown>;
};

const SECTIONS: { group: string; items: Section[] }[] = [
  {
    group: "Förder-Regelwerk",
    items: [
      { key: "subsidy_programs", label: "Förderprogramme", table: "subsidy_programs",
        template: { code: "KFW-XXX", name: "", authority: "KfW", level: "bund", is_active: true, trust_level: "C", current_version: 1 } },
      { key: "subsidy_rules", label: "Förderregeln", table: "subsidy_rules",
        template: { program_id: "", rule_type: "eligibility", name: "", logic: {}, priority: 100, is_active: true, version: 1 } },
      { key: "subsidy_sources", label: "Förderquellen", table: "subsidy_sources",
        template: { source_type: "official", url: "", publisher: "", trust_level: "C" } },
    ],
  },
  {
    group: "Steuer & Berechnung",
    items: [
      { key: "tax_rules", label: "Steuerregeln", table: "tax_rules",
        template: { code: "AFA_PV", name: "", version: 1, logic: {}, is_active: true, trust_level: "C" } },
      { key: "calc_ruleset_versions", label: "Rulesets", table: "calc_ruleset_versions",
        template: { domain: "pv", version: "1.0.0", is_active: false } },
      { key: "calc_formulas", label: "Formeln", table: "calc_formulas",
        template: { ruleset_version_id: "", code: "", name: "", domain: "pv", expression: "", inputs: [], outputs: [], trust_level: "B", requires_expert: false } },
      { key: "energy_price_scenarios", label: "Energiepreis-Szenarien", table: "energy_price_scenarios",
        template: { name: "Basisszenario", scenario_type: "base", electricity_price_ct_kwh: 35, escalation_electricity_pct: 3, trust_level: "C", is_active: true } },
    ],
  },
  {
    group: "Produkte",
    items: [
      { key: "manufacturers", label: "Hersteller", table: "manufacturers",
        template: { name: "", slug: "", country: "DE", is_active: true, trust_level: "C" } },
      { key: "product_groups", label: "Produktgruppen", table: "product_groups",
        template: { code: "", name: "", sort_order: 100, is_active: true } },
      { key: "products", label: "Produkte", table: "products",
        template: { manufacturer_id: "", group_id: "", sku: "", name: "", specs: {}, is_active: true, is_ekd: false, trust_level: "C" } },
      { key: "product_prices", label: "Produktpreise", table: "product_prices",
        template: { product_id: "", price_type: "list", currency: "EUR", net_amount: 0, vat_rate: 19, is_active: true, trust_level: "C" } },
      { key: "product_warranties", label: "Garantien", table: "product_warranties",
        template: { product_id: "", warranty_type: "product", years: 10, is_ekd: false, trust_level: "C" } },
      { key: "product_compatibility", label: "Kompatibilitätsregeln", table: "product_compatibility",
        template: { product_a_id: "", product_b_id: "", rule_type: "allow", condition: {}, priority: 100, is_active: true } },
      { key: "ekd_products", label: "EKD-Produkte", table: "ekd_products",
        template: { code: "EKD-PV-30", name: "", warranty_years: 30, scope: {}, is_active: true, trust_level: "C" } },
    ],
  },
  {
    group: "Plattform",
    items: [
      { key: "prompts", label: "Prompt-Registry", table: "prompts",
        template: { name: "", domain: "analysis", version: 1, model: "google/gemini-2.5-flash", system_prompt: "", is_active: true } },
      { key: "feature_flags", label: "Feature-Flags", table: "feature_flags",
        template: { key: "", enabled: false, rollout_percent: 0, condition: {} } },
      { key: "system_config", label: "Systemkonfiguration", table: "system_config",
        template: { key: "", value: {}, value_type: "json", category: "general", is_secret: false } },
    ],
  },
];

const NAV = [
  { to: "/app/crm", label: "CRM" },
  { to: "/app/admin", label: "Admin" },
];

function AdminCenter() {
  const [section, setSection] = useState<Section>(SECTIONS[0].items[0]);
  return (
    <PortalShell nav={NAV}>
      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-3 space-y-4">
          {SECTIONS.map((g) => (
            <div key={g.group}>
              <div className="text-xs uppercase text-muted-foreground mb-2">{g.group}</div>
              <ul className="space-y-1">
                {g.items.map((s) => (
                  <li key={s.key}>
                    <button
                      onClick={() => setSection(s)}
                      className={`w-full text-left px-3 py-1.5 rounded text-sm ${section.key === s.key ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"}`}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>
        <section className="col-span-9">
          <h1 className="text-2xl font-semibold mb-1">{section.label}</h1>
          <p className="text-sm text-muted-foreground mb-4">Tabelle: <code>{section.table}</code></p>
          <AdminTable section={section} />
        </section>
      </div>
    </PortalShell>
  );
}

function AdminTable({ section }: { section: Section }) {
  const qc = useQueryClient();
  const list = useServerFn(adminList);
  const upsert = useServerFn(adminUpsert);
  const del = useServerFn(adminDelete);
  const activate = useServerFn(activateRuleset);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", section.table],
    queryFn: () => list({ data: { table: section.table, limit: 200, orderBy: "created_at", ascending: false } }),
  });

  const [draft, setDraft] = useState<string>(JSON.stringify(section.template ?? {}, null, 2));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      let record: Record<string, unknown>;
      try { record = JSON.parse(draft); } catch (e) { throw new Error("Ungültiges JSON: " + (e as Error).message); }
      if (editingId) record.id = editingId;
      return upsert({ data: { table: section.table, record } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", section.table] }); setError(null); },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { table: section.table, id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", section.table] }),
  });

  const doActivate = useMutation({
    mutationFn: (id: string) => activate({ data: { rulesetId: id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", section.table] }),
  });

  const rows = (data?.rows ?? []) as Record<string, unknown>[];

  return (
    <div className="space-y-4">
      <div className="border rounded p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-sm">{editingId ? "Datensatz bearbeiten" : "Neuer Datensatz"}</h2>
          <div className="flex gap-2">
            {editingId && (
              <Button variant="ghost" size="sm" onClick={() => { setEditingId(null); setDraft(JSON.stringify(section.template ?? {}, null, 2)); }}>
                Abbrechen
              </Button>
            )}
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Speichere…" : editingId ? "Aktualisieren" : "Anlegen"}
            </Button>
          </div>
        </div>
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={10} className="font-mono text-xs" />
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <div className="border rounded">
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <span className="text-sm font-medium">Einträge ({rows.length})</span>
        </div>
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Lade…</div>
        ) : (
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={String(r.id)} className="px-4 py-3 flex items-start justify-between gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {String(r.name ?? r.code ?? r.key ?? r.sku ?? r.id)}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {Object.entries(r).filter(([k]) => !["id","created_at","updated_at","metadata"].includes(k)).slice(0, 6).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v).slice(0,40) : String(v)}`).join(" · ")}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {section.table === "calc_ruleset_versions" && !r.is_active && (
                    <Button size="sm" variant="outline" onClick={() => doActivate.mutate(String(r.id))} disabled={doActivate.isPending}>
                      Aktivieren
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setEditingId(String(r.id)); setDraft(JSON.stringify(r, null, 2)); }}>
                    Bearbeiten
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => { if (confirm("Wirklich löschen?")) remove.mutate(String(r.id)); }}>
                    Löschen
                  </Button>
                </div>
              </li>
            ))}
            {rows.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">Keine Einträge.</li>}
          </ul>
        )}
      </div>
    </div>
  );
}
